export interface ReportNumberParsingOptions {
   removeGroupingSeparators?: boolean;
}

export interface ReportCurrencyFormattingOptions extends ReportNumberParsingOptions {
   locale?: string;
   currencySymbol?: string;
   minimumFractionDigits?: number;
   maximumFractionDigits?: number;
}

export function asString(value: unknown): string {
   if (value === null || value === undefined) {
      return '';
   }

   return String(value);
}

export function asNumberOrNull(
   value: unknown,
   options: ReportNumberParsingOptions = {}
): number | null {
   if (value === null || value === undefined || value === '') {
      return null;
   }

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   let normalisedValue = String(value);

   if (options.removeGroupingSeparators) {
      normalisedValue = normalisedValue
         .replace(/,/g, '')
         .replace(/\s+/g, '')
         .replace(/^-\s*/, '-');
   }

   const amount = Number(normalisedValue);

   return Number.isFinite(amount) ? amount : null;
}

export function formatCurrency(
   value: unknown,
   options: ReportCurrencyFormattingOptions = {}
): string {
   const amount = asNumberOrNull(value, options);

   if (amount === null) {
      return '';
   }

   const {
      locale = 'en-AU',
      currencySymbol = '$',
      minimumFractionDigits = 2,
      maximumFractionDigits = 2
   } = options;

   const formattedAmount = Math.abs(amount).toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits
   });

   return amount < 0
      ? `-${currencySymbol}${formattedAmount}`
      : `${currencySymbol}${formattedAmount}`;
}

export function formatDateTime(value: unknown): string {
   const rawValue = asString(value).trim();

   if (!rawValue) {
      return '';
   }

   const dateTimeMatch = rawValue.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/
   );

   if (!dateTimeMatch) {
      return rawValue;
   }

   const [, year, month, day, hour, minute, second] = dateTimeMatch;

   return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
}

export function formatDate(value: unknown): string {
   const rawValue = asString(value).trim();

   if (!rawValue) {
      return '';
   }

   const isoDateMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

   if (!isoDateMatch) {
      return rawValue;
   }

   return `${isoDateMatch[3]}/${isoDateMatch[2]}/${isoDateMatch[1]}`;
}

export function formatFlexibleDate(value: unknown): string {
   const rawValue = asString(value).trim();

   if (!rawValue) {
      return '';
   }

   const formattedIsoDate = formatDate(rawValue);

   if (formattedIsoDate !== rawValue) {
      return formattedIsoDate;
   }

   const slashDateMatch = rawValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);

   if (slashDateMatch) {
      return `${slashDateMatch[1].padStart(
         2,
         '0'
      )}/${slashDateMatch[2].padStart(2, '0')}/${slashDateMatch[3]}`;
   }

   return rawValue;
}

export function formatTime(value: unknown): string {
   const rawValue = asString(value).trim();

   if (!rawValue) {
      return '';
   }

   const isoTimeMatch = rawValue.match(/T(\d{2}:\d{2})/);

   if (isoTimeMatch) {
      return isoTimeMatch[1];
   }

   const simpleTimeMatch = rawValue.match(/^(\d{1,2}):(\d{2})/);

   if (simpleTimeMatch) {
      return `${simpleTimeMatch[1].padStart(2, '0')}:${simpleTimeMatch[2]}`;
   }

   return rawValue;
}
